import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Document Search Contract
 *
 * Unified search for POs, SOs, and Shipments to use in AP invoice matching.
 * Supports searching across all supported document types with relevance ranking.
 */

const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});

/**
 * Document type enum for search results
 */
export const DocumentTypeSchema = z.enum([
  "PURCHASE_ORDER",
  "SALES_ORDER",
  "SHIPMENT",
]);

export type DocumentType = z.infer<typeof DocumentTypeSchema>;

/**
 * PO status enum (for document search)
 */
export const DocumentSearchPOStatusSchema = z.enum([
  "PENDING_CONFIRMATION",
  "CONFIRMED",
  "DRAFT",
  "PENDING_APPROVAL",
  "APPROVED",
  "SENT",
  "PARTIALLY_RECEIVED",
  "FULLY_RECEIVED",
  "PARTIALLY_INVOICED",
  "FULLY_INVOICED",
  "CLOSED",
  "CANCELLED",
]);

/**
 * SO status enum (for document search)
 */
export const DocumentSearchSOStatusSchema = z.enum([
  "pending",
  "in_progress",
  "fulfilled",
  "closed",
]);

/**
 * Shipment status enum (for document search)
 */
export const DocumentSearchShipmentStatusSchema = z.enum([
  "need_to_be_scheduled",
  "scheduled",
  "in_transit",
  "delivered",
  "cancelled",
]);

/**
 * Search mode - determines what entity types to search
 */
export const SearchModeSchema = z.enum([
  "ALL", // Search POs, SOs, and Shipments
  "PURCHASE_ORDERS_ONLY", // Only search POs (when supplier is selected)
  "SALES_ORDERS_ONLY", // Only search SOs (when customer is selected)
  "SHIPMENTS_ONLY", // Only search Shipments
]);

export type SearchMode = z.infer<typeof SearchModeSchema>;

/**
 * Search input schema
 */
export const SearchDocumentsInputSchema = z.object({
  // Free-text query (searches reference numbers, descriptions, SKUs)
  query: z.string().optional(),

  // Filter by mode (when supplier/customer is selected)
  mode: SearchModeSchema.optional().prefault("ALL"),

  // Context for ranking (from invoice)
  invoiceId: UUIDSchema.optional(),
  invoiceDate: z.string().optional(), // ISO date
  invoiceAmount: DecimalStringSchema.optional(),

  // Filter by supplier (switches to PO mode)
  supplierId: UUIDSchema.optional(),

  // Filter by customer (switches to SO mode)
  customerId: UUIDSchema.optional(),

  // Date range filters
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),

  // Amount range filters
  amountMin: DecimalStringSchema.optional(),
  amountMax: DecimalStringSchema.optional(),

  // Status filters
  poStatuses: z.array(DocumentSearchPOStatusSchema).optional(),
  soStatuses: z.array(DocumentSearchSOStatusSchema).optional(),
  shipmentStatuses: z.array(DocumentSearchShipmentStatusSchema).optional(),

  // Pagination
  limit: z.int().min(1).max(100).optional().prefault(25),
  offset: z.int().min(0).optional().prefault(0),
});

/**
 * Matched line item schema (for when query matches line items)
 */
export const MatchedLineItemSchema = z.object({
  id: UUIDSchema,
  lineNumber: z.int().nullable(),
  description: z.string().nullable(),
  sku: z.string().nullable().optional(),
  quantity: z.union([z.number(), z.string()]).nullable().optional(),
  unitPrice: DecimalStringSchema.nullable().optional(),
  totalPrice: DecimalStringSchema.nullable().optional(),
});

/**
 * Charge summary schema (for showing SO charges)
 */
export const ChargeSummarySchema = z.object({
  id: UUIDSchema,
  categoryLabel: z.string().nullable(),
  amount: DecimalStringSchema,
  direction: z.enum(["DEBIT", "CREDIT"]),
});

/**
 * Unified search result item
 */
export const SearchDocumentResultSchema = z.object({
  // Common fields
  id: UUIDSchema,
  documentType: DocumentTypeSchema,
  referenceNumber: z.string(), // externalId for PO, originId or internalId for SO, originLoadNumber for Shipment
  orderDate: z.string(),
  totalAmount: DecimalStringSchema.nullable(),
  currency: z.string().nullable().optional(),
  relevanceScore: z.number(),

  // For Purchase Orders
  supplierId: UUIDSchema.nullable().optional(),
  supplierName: z.string().nullable().optional(),
  poStatus: DocumentSearchPOStatusSchema.nullable().optional(),
  poOriginId: z.string().nullable().optional(),
  poOriginSystem: z.string().nullable().optional(),

  // For Sales Orders
  customerId: UUIDSchema.nullable().optional(),
  customerName: z.string().nullable().optional(),
  soStatus: DocumentSearchSOStatusSchema.nullable().optional(),
  customerPurchaseOrderNumber: z.string().nullable().optional(),
  soOriginId: z.string().nullable().optional(),
  soOriginSystem: z.string().nullable().optional(),

  // For Shipments
  shipmentStatus: DocumentSearchShipmentStatusSchema.nullable().optional(),
  shipmentType: z.enum(["incoming", "outgoing"]).nullable().optional(),
  origin: z.string().nullable().optional(),
  destination: z.string().nullable().optional(),
  originLoadId: z.string().nullable().optional(),
  originLoadNumber: z.string().nullable().optional(),
  shipmentOriginSystem: z.string().nullable().optional(),

  // Matched items (when query provided)
  matchedLineItems: z.array(MatchedLineItemSchema).optional(),

  // Charges summary (mainly for SOs and Shipments)
  chargesCount: z.int().optional(),
  chargesSummary: z.array(ChargeSummarySchema).optional(),

  // Line items count
  lineItemsCount: z.int().optional(),
});

export const SearchDocumentsResponseSchema = z.object({
  results: z.array(SearchDocumentResultSchema),
  pagination: z.object({
    total: z.int(),
    limit: z.int(),
    offset: z.int(),
    hasMore: z.boolean(),
  }),
});

/**
 * Contract definition
 */
export const documentSearchContracts = {
  search: oc
    .input(SearchDocumentsInputSchema)
    .output(SearchDocumentsResponseSchema)
    .route({
      method: "GET",
    }),
} as const;

// Type exports
export type SearchDocumentsInput = z.infer<typeof SearchDocumentsInputSchema>;
export type SearchDocumentsResponse = z.infer<
  typeof SearchDocumentsResponseSchema
>;
export type SearchDocumentResult = z.infer<typeof SearchDocumentResultSchema>;
export type MatchedLineItem = z.infer<typeof MatchedLineItemSchema>;
export type ChargeSummary = z.infer<typeof ChargeSummarySchema>;
